import { getDeviceFirmwareUpgradeStatus } from '@/models/device/utilities/get-device-firmware-upgrade-status';
import { qx_ePACSStatus_enum, qx_eDeviceConnectionStatus_enum, qx_eDeviceKind_enum } from '@/graphql/types';
import { FaIconComplex } from '@/types';
import { ICON } from '@/constants/icons';
import { AppColor } from '@/types/colors';
import { DeviceWiringTableRow } from '@/types/devices';

/**
 * Device make/brand constants
 * Centralized for consistency across the application
 */
export const DEVICE_MAKE = {
  MERCURY: 'Mercury',
  ROSSLARE: 'Rosslare',
  AXIS: 'Axis'
} as const;

export const DEVICE_WIRING_SEARCH_FIELDS: (keyof DeviceWiringTableRow)[] = [
  'inventoryName',
  'inventoryAddress',
  'type',
  'port',
  'door',
  'component',
  'ref'
];

export const DEVICE_DEFAULT_ICON = 'sensor';
export const DEVICE_DEFAULT_ICON_OFF = 'sensor-off';
export const DEVICE_DEFAULT_DIO_ICON = 'arrows-turn-to-dots';
export const DEVICE_DEFAULT_INPUT_ICON = 'arrow-right-to-arc';
export const DEVICE_DEFAULT_OUTPUT_ICON = 'arrow-right-from-arc';
export const DEVICE_SIO_DEFAULT_ICON = 'sensor';

/**
 * DIO Type specific icons
 */
export const DEVICE_RELAY_ICON = 'bolt';
export const DEVICE_REX_ICON = 'hand-pointer';
export const DEVICE_WIEGAND_ICON = 'id-card-clip';

/**
 * Device status colors
 * Using AppColor type for consistency
 */
export const DEVICE_STATUS_COLORS: Record<string, AppColor> = {
  DANGER: 'danger',
  WARN: 'warn',
  GREY: 'grey',
  INFO: 'info',
  SUCCESS: 'success'
} as const;

/**
 * Device status tooltip messages
 * Centralized for consistency and future i18n support
 */
export const DEVICE_STATUS_TOOLTIPS = {
  // Connection statuses
  GATEWAY_DISCONNECTED: 'Gateway Disconnected',
  DEVICE_DISCONNECTED: 'Device Disconnected',
  DELETING: 'Deleting...',
  DELETE_ON_EXPIRE: 'Device will be deleted when all video in cloud storage has expired.',
  AWAITING_EVENTS: 'Awaiting Events',
  EVENT_SUBSCRIPTION_FAILED: 'Event Subscription Failed',
  UNKNOWN: 'Unknown',
  INVALID_CREDENTIALS: 'Invalid Credentials',
  NOT_SUPPORTED: 'Not Supported',
  REBOOTING: 'Rebooting',
  CONNECTING: 'Connecting',
  DISABLED: 'Disabled',
  OFFLINE: qx_eDeviceConnectionStatus_enum.Offline,
  DISCONNECTED: qx_eDeviceConnectionStatus_enum.Disconnected,
  NO_EVENTS: 'No Event Recording',
  UPGRADE_NEEDED: 'Device Requires an Upgrade',
  UPGRADING: 'Upgrading',
  CONNECTED: qx_eDeviceConnectionStatus_enum.Connected,
  ONLINE: qx_eDeviceConnectionStatus_enum.Online,

  // Kind-specific
  OFFLINE_LOCK: 'Offline Lock',

  // Brand-specific
  MAINTENANCE_MODE: 'Maintenance Mode',
  BOARD_NOT_AVAILABLE: 'Board Not Available',

  // PACS statuses
  CONFIGURING: 'Configuring',
  NEEDS_CONFIGURATION: 'Configuration Required',
  CONFIGURATION_FAILED: 'Configuration Failed',
  VALIDATE_FAILED: 'Validation Failed',
  NEEDS_UPDATE: 'Update Required',
  UPDATE_FAILED: 'Update Failed',
  UPDATING_CONFIG: 'Updating Configuration'
} as const;

/**
 * Device connection status icon configurations
 */
const CONNECTION_STATUS_MAP: Record<string, FaIconComplex> = {
  [qx_eDeviceConnectionStatus_enum.Deleting]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.DELETING,
    color: DEVICE_STATUS_COLORS.DANGER,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON_OFF,
    mask: 'circle',
    transform: 'shrink-6 right-6 up-4',
    subColor: 'white',
    subIcon: 'spinner',
    subTransform: 'shrink-8 right-6 up-4',
    subClass: 'fa-pulse'
  },
  [qx_eDeviceConnectionStatus_enum.DeleteOnCloudStorageExpire]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.DELETE_ON_EXPIRE,
    color: DEVICE_STATUS_COLORS.DANGER,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON_OFF,
    mask: 'triangle',
    transform: 'shrink-4 right-7 up-5',
    opacity: '1',
    subIcon: 'triangle-exclamation',
    subColor: DEVICE_STATUS_COLORS.INFO,
    subTransform: 'shrink-8 right-6 up-4'
  },
  [qx_eDeviceConnectionStatus_enum.AwaitingEvents]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.AWAITING_EVENTS,
    color: DEVICE_STATUS_COLORS.WARN,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON
  },
  [qx_eDeviceConnectionStatus_enum.EventSubscriptionFailed]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.EVENT_SUBSCRIPTION_FAILED,
    color: DEVICE_STATUS_COLORS.WARN,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON
  },
  [qx_eDeviceConnectionStatus_enum.Unknown]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.UNKNOWN,
    color: DEVICE_STATUS_COLORS.GREY,
    icon: DEVICE_DEFAULT_ICON,
    mask: 'triangle',
    style: { transform: 'scale(1.05) !important' },
    iconStyle: 'fas',
    subColor: DEVICE_STATUS_COLORS.WARN,
    subIcon: 'question',
    subMask: 'triangle',
    subStyle: { transform: 'scale(0.9) !important' },
    subTransform: 'shrink-7 down-2',
    transform: 'grow-2'
  },
  [qx_eDeviceConnectionStatus_enum.InvalidCredentials]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.INVALID_CREDENTIALS,
    color: DEVICE_STATUS_COLORS.GREY,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON,
    mask: 'triangle',
    transform: 'grow-2',
    opacity: '1',
    subColor: DEVICE_STATUS_COLORS.DANGER,
    subIcon: 'triangle-exclamation',
    subTransform: 'shrink-2'
  },
  [qx_eDeviceConnectionStatus_enum.NotSupported]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.NOT_SUPPORTED,
    color: DEVICE_STATUS_COLORS.GREY,
    icon: DEVICE_DEFAULT_ICON,
    mask: 'triangle',
    style: { transform: 'scale(1.05) !important' },
    iconStyle: 'fas',
    subColor: DEVICE_STATUS_COLORS.DANGER,
    subIcon: 'xmark',
    subMask: 'triangle',
    subStyle: { transform: 'scale(0.9) !important' },
    subTransform: 'shrink-7 down-2',
    transform: 'grow-2'
  },
  [qx_eDeviceConnectionStatus_enum.Rebooting]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.REBOOTING,
    color: DEVICE_STATUS_COLORS.INFO,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON,
    mask: 'circle',
    transform: 'shrink-4',
    subColor: 'blue',
    subIcon: 'rotate-right',
    subTransform: 'shrink-6',
    subClass: 'fa-spin'
  },
  [qx_eDeviceConnectionStatus_enum.Connecting]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.CONNECTING,
    color: DEVICE_STATUS_COLORS.INFO,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON,
    mask: 'circle',
    transform: 'shrink-6 right-6 up-4',
    subColor: 'white',
    subIcon: 'spinner',
    subTransform: 'shrink-8 right-6 up-4',
    subClass: 'fa-pulse'
  },
  [qx_eDeviceConnectionStatus_enum.Offline]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.OFFLINE,
    color: DEVICE_STATUS_COLORS.DANGER,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON_OFF
  },
  [qx_eDeviceConnectionStatus_enum.Disconnected]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.DISCONNECTED,
    color: DEVICE_STATUS_COLORS.DANGER,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON_OFF
  },
  [qx_eDeviceConnectionStatus_enum.NoEvents]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.NO_EVENTS,
    color: DEVICE_STATUS_COLORS.WARN,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON
  },
  [qx_eDeviceConnectionStatus_enum.UpgradeNeeded]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.UPGRADE_NEEDED,
    color: DEVICE_STATUS_COLORS.WARN,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON
  },
  [qx_eDeviceConnectionStatus_enum.Upgrading]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.UPGRADING,
    color: DEVICE_STATUS_COLORS.WARN,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON
  },
  [qx_eDeviceConnectionStatus_enum.Connected]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.CONNECTED,
    color: DEVICE_STATUS_COLORS.SUCCESS,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON
  },
  [qx_eDeviceConnectionStatus_enum.Online]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.ONLINE,
    color: DEVICE_STATUS_COLORS.SUCCESS,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON
  },
  // Special string status (not in enum)
  Disabled: {
    tooltip: DEVICE_STATUS_TOOLTIPS.DISABLED,
    color: DEVICE_STATUS_COLORS.GREY,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON_OFF
  }
};

/**
 * PACS status icon configurations
 */
const PACS_STATUS_MAP: Record<string, FaIconComplex> = {
  [`hardware_${qx_ePACSStatus_enum.Updating}`]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.CONFIGURING,
    color: DEVICE_STATUS_COLORS.GREY,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON,
    mask: 'triangle',
    transform: 'grow-2',
    opacity: '1',
    subColor: DEVICE_STATUS_COLORS.INFO,
    subIcon: 'triangle-exclamation',
    subTransform: 'shrink-2'
  },
  [`hardware_${qx_ePACSStatus_enum.NeedsUpdate}`]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.NEEDS_UPDATE,
    color: DEVICE_STATUS_COLORS.GREY,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON,
    mask: 'triangle',
    transform: 'grow-2',
    opacity: '1',
    subIcon: 'triangle-exclamation',
    subColor: DEVICE_STATUS_COLORS.WARN,
    subTransform: 'shrink-2'
  },
  [`hardware_${qx_ePACSStatus_enum.UpdateFailed}`]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.CONFIGURATION_FAILED,
    color: DEVICE_STATUS_COLORS.GREY,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON,
    mask: 'triangle',
    transform: 'grow-2',
    opacity: '1',
    subColor: DEVICE_STATUS_COLORS.DANGER,
    subIcon: 'triangle-exclamation',
    subTransform: 'shrink-2'
  },
  [qx_ePACSStatus_enum.ValidateFailed]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.VALIDATE_FAILED,
    color: DEVICE_STATUS_COLORS.GREY,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON,
    mask: 'shield',
    transform: 'grow-2',
    opacity: '1',
    subColor: DEVICE_STATUS_COLORS.DANGER,
    subIcon: 'shield-exclamation',
    subTransform: 'shrink-2'
  },
  [qx_ePACSStatus_enum.NeedsUpdate]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.NEEDS_UPDATE,
    color: DEVICE_STATUS_COLORS.GREY,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON,
    mask: 'triangle',
    transform: 'grow-2',
    opacity: '1',
    subIcon: 'triangle-exclamation',
    subColor: DEVICE_STATUS_COLORS.WARN,
    subTransform: 'shrink-2'
  },
  [qx_ePACSStatus_enum.NeedsUpdateOnConnect]: {
    tooltip: 'Update Required When Connected',
    color: DEVICE_STATUS_COLORS.GREY,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON,
    mask: 'triangle',
    transform: 'grow-2',
    opacity: '1',
    subIcon: 'triangle-exclamation',
    subColor: DEVICE_STATUS_COLORS.WARN,
    subTransform: 'shrink-2'
  },
  [qx_ePACSStatus_enum.UpdateFailed]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.UPDATE_FAILED,
    color: DEVICE_STATUS_COLORS.GREY,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON,
    mask: 'triangle',
    transform: 'grow-2',
    opacity: '1',
    subColor: DEVICE_STATUS_COLORS.DANGER,
    subIcon: 'triangle-exclamation',
    subTransform: 'shrink-2'
  },
  [qx_ePACSStatus_enum.Updating]: {
    tooltip: DEVICE_STATUS_TOOLTIPS.UPDATING_CONFIG,
    color: DEVICE_STATUS_COLORS.GREY,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON,
    mask: 'triangle',
    transform: 'grow-2',
    opacity: '1',
    subColor: DEVICE_STATUS_COLORS.INFO,
    subIcon: 'triangle-exclamation',
    subTransform: 'shrink-2'
  }
};

/**
 * Offline locks never connect to a gateway, so their status icon is fixed: the device
 * icon with the same wifi-slash badge as the offline lock door icon.
 */
export const OFFLINE_LOCK_STATUS_ICON: FaIconComplex = {
  tooltip: DEVICE_STATUS_TOOLTIPS.OFFLINE_LOCK,
  // No colour: it takes the surrounding text colour, so it reads on light and dark rows alike.
  iconStyle: 'fas',
  icon: DEVICE_DEFAULT_ICON,
  badge: { icon: ICON.WIFI_SLASH, position: 'left' }
};

/**
 * Kinds whose status icon is fixed regardless of connection state.
 */
const DEVICE_KIND_STATUS_ICON = new Map<string, FaIconComplex>([
  [qx_eDeviceKind_enum.OfflineLock, OFFLINE_LOCK_STATUS_ICON]
]);

export const getDeviceKindStatusIcon = (kind?: string | null): FaIconComplex | undefined =>
  DEVICE_KIND_STATUS_ICON.get(kind ?? '');

export const getDeviceStatusIcon = (
  gatewayConnected: boolean | undefined,
  connection: qx_eDeviceConnectionStatus_enum | string,
  pacs: qx_ePACSStatus_enum,
  pacsHardware: qx_ePACSStatus_enum,
  deviceMake?: string,
  deviceMeta?: any
): FaIconComplex => {
  // Handle synthetic UI statuses (created by SioSmartIcon as fallbacks)
  if (connection === 'GatewayOffline') {
    return {
      tooltip: DEVICE_STATUS_TOOLTIPS.GATEWAY_DISCONNECTED,
      color: DEVICE_STATUS_COLORS.GREY,
      icon: DEVICE_DEFAULT_ICON_OFF,
      iconStyle: 'fas'
    };
  }

  if (connection === 'Disabled') {
    return {
      tooltip: DEVICE_STATUS_TOOLTIPS.DISABLED,
      color: DEVICE_STATUS_COLORS.GREY,
      icon: DEVICE_DEFAULT_ICON_OFF,
      iconStyle: 'fas'
    };
  }

  if (connection === 'DeviceOffline') {
    return {
      tooltip: DEVICE_STATUS_TOOLTIPS.DEVICE_DISCONNECTED,
      color: DEVICE_STATUS_COLORS.GREY,
      icon: DEVICE_DEFAULT_ICON_OFF,
      iconStyle: 'fas'
    };
  }

  // Gateway check (takes precedence over connection status)
  if (!gatewayConnected) {
    return {
      tooltip: DEVICE_STATUS_TOOLTIPS.GATEWAY_DISCONNECTED,
      color: DEVICE_STATUS_COLORS.GREY,
      icon: DEVICE_DEFAULT_ICON_OFF,
      iconStyle: 'fas'
    };
  }

  // Brand-specific: Detached status
  if (connection === qx_eDeviceConnectionStatus_enum.Detached) {
    const detachedTooltip =
      deviceMake === 'Mercury' ? DEVICE_STATUS_TOOLTIPS.MAINTENANCE_MODE : DEVICE_STATUS_TOOLTIPS.BOARD_NOT_AVAILABLE;
    return {
      tooltip: detachedTooltip,
      color: DEVICE_STATUS_COLORS.WARN,
      icon: DEVICE_DEFAULT_ICON_OFF,
      iconStyle: 'fas'
    };
  }

  // Get base connection status first
  const baseConnectionStatus = connection && CONNECTION_STATUS_MAP[connection] ? CONNECTION_STATUS_MAP[connection] : {
    tooltip: false,
    color: DEVICE_STATUS_COLORS.GREY,
    iconStyle: 'fas',
    icon: DEVICE_DEFAULT_ICON
  };

  // Check for firmware upgrade required/available (device.meta + meta.extensions[])
  const firmwareStatus = getDeviceFirmwareUpgradeStatus(deviceMeta);
  const isConnectedStatus = connection === qx_eDeviceConnectionStatus_enum.Connected;

  if (firmwareStatus.upgradeRequired) {
    return {
      ...baseConnectionStatus,
      tooltip: `${baseConnectionStatus.tooltip} - Firmware Upgrade Required`,
      mask: 'circle',
      transform: 'shrink-6 right-6 up-4',
      subColor: DEVICE_STATUS_COLORS.DANGER,
      subIcon: 'arrow-up-from-arc',
      subTransform: 'shrink-8 right-6 up-4'
    };
  }

  if (firmwareStatus.upgradeAvailable) {
    return {
      ...baseConnectionStatus,
      tooltip: `${baseConnectionStatus.tooltip} - Firmware Upgrade Available`,
      mask: 'circle',
      transform: 'shrink-6 right-6 up-4',
      subColor: DEVICE_STATUS_COLORS.WARN,
      subIcon: 'arrow-up-from-arc',
      subTransform: 'shrink-8 right-6 up-4'
    };
  }

  // PACS statuses should only affect the icon/tooltip when the device is connected.
  if (isConnectedStatus && pacsHardware && pacsHardware !== qx_ePACSStatus_enum.Unknown) {
    const hardwareKey = `hardware_${pacsHardware}`;
    const pacsHardwareStatus = PACS_STATUS_MAP[hardwareKey];
    if (pacsHardwareStatus) {
      return {
        ...baseConnectionStatus,
        tooltip: `${baseConnectionStatus.tooltip} - ${pacsHardwareStatus.tooltip}`,
        mask: pacsHardwareStatus.mask,
        transform: pacsHardwareStatus.transform,
        opacity: pacsHardwareStatus.opacity,
        subColor: pacsHardwareStatus.subColor,
        subIcon: pacsHardwareStatus.subIcon,
        subTransform: pacsHardwareStatus.subTransform
      };
    }
  }

  // PACS statuses should only affect the icon/tooltip when the device is connected.
  if (isConnectedStatus && pacs && pacs !== qx_ePACSStatus_enum.Unknown && PACS_STATUS_MAP[pacs]) {
    const pacsStatus = PACS_STATUS_MAP[pacs];
    return {
      ...baseConnectionStatus,
      tooltip: `${baseConnectionStatus.tooltip} - ${pacsStatus.tooltip}`,
      mask: pacsStatus.mask,
      transform: pacsStatus.transform,
      opacity: pacsStatus.opacity,
      subColor: pacsStatus.subColor,
      subIcon: pacsStatus.subIcon,
      subMask: pacsStatus.subMask,
      subTransform: pacsStatus.subTransform
    };
  }

  // Return base connection status if no PACS overlays
  return baseConnectionStatus;
};
